// WASM Loader for morm_signer
// This handles loading and initializing the Go WASM runtime

import { WasmModule, GoRuntime } from "../types";

let wasmModule: WasmModule = {
  instance: null,
  module: null,
};

let go: GoRuntime | null = null;

// Initialize the Go runtime
export async function initWasm(): Promise<boolean> {
  try {
    // Load wasm_exec.js from CDN or local
    if (!window.Go) {
      console.log("Loading wasm_exec.js...");

      // Try multiple sources with local file first, then CDN fallbacks
      const cdnSources = [
        "/wasm_exec.js", // Local file (preferred)
        "https://cdn.jsdelivr.net/gh/golang/go@go1.21.0/misc/wasm/wasm_exec.js",
        "https://cdn.jsdelivr.net/gh/golang/go@go1.20.0/misc/wasm/wasm_exec.js",
        "https://unpkg.com/@go-wasm/wasm_exec@latest/wasm_exec.js",
      ];

      let loaded = false;
      let lastError: Error | null = null;

      for (const src of cdnSources) {
        try {
          console.log(`Trying to load wasm_exec.js from: ${src}`);
          await new Promise<void>((resolve, reject) => {
            const script = document.createElement("script");
            script.src = src;
            script.async = true;
            script.onload = () => {
              console.log(`Successfully loaded wasm_exec.js from: ${src}`);
              resolve();
            };
            script.onerror = () => {
              reject(new Error(`Failed to load from ${src}`));
            };
            document.head.appendChild(script);
          });
          loaded = true;
          break;
        } catch (error: any) {
          console.warn(`Failed to load from ${src}:`, error?.message);
          lastError = error;
          // Remove the failed script
          const scripts = document.head.querySelectorAll(
            `script[src="${src}"]`,
          );
          scripts.forEach((s) => s.remove());
        }
      }

      if (!loaded) {
        throw new Error(
          `Failed to load wasm_exec.js from all sources. ` +
            `Last error: ${lastError?.message || "Unknown error"}. ` +
            `Please ensure wasm_exec.js is available. You can download it from: ` +
            `https://github.com/golang/go/blob/master/misc/wasm/wasm_exec.js`,
        );
      }

      console.log("wasm_exec.js loaded successfully");
    } else {
      console.log("wasm_exec.js already loaded");
    }

    // Set up the Go runtime
    const Go = window.Go;
    if (!Go) {
      throw new Error(
        "Go runtime not available. Make sure wasm_exec.js is loaded.",
      );
    }
    console.log("Go runtime available");

    // Use local variable for better type narrowing
    const goInstance = new Go();
    go = goInstance;
    console.log("Go instance created");

    // Load the WASM module
    const wasmPath = "/morm_signer.wasm";
    console.log("Loading WASM from:", wasmPath);

    let wasmBytes: ArrayBuffer;
    try {
      const response = await fetch(wasmPath);
      console.log("WASM fetch response:", {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok,
        url: response.url,
      });

      if (!response.ok) {
        const errorText = await response
          .text()
          .catch(() => "Unable to read error response");
        throw new Error(
          `Failed to load WASM from ${wasmPath}: ${response.status} ${response.statusText}. ` +
            `Make sure the morm_signer.wasm file is available in the public directory.`,
        );
      }
      wasmBytes = await response.arrayBuffer();
      console.log(
        "WASM file loaded successfully, size:",
        wasmBytes.byteLength,
        "bytes",
      );
    } catch (error: any) {
      console.error("WASM fetch failed:", error);
      throw new Error(
        `Failed to fetch WASM file: ${error?.message || error}. ` +
          `The file should be accessible at ${window.location.origin}${wasmPath}`,
      );
    }

    // Instantiate the WASM module
    console.log("Instantiating WASM module...");
    let result: WebAssembly.WebAssemblyInstantiatedSource;
    try {
      result = await WebAssembly.instantiate(
        wasmBytes,
        goInstance.importObject,
      );
      console.log("WASM module instantiated successfully");
    } catch (error: any) {
      console.error("Failed to instantiate WASM:", error);
      throw new Error(
        `Failed to instantiate WASM module: ${error?.message || error}. ` +
          `The WASM file may be corrupted or incompatible.`,
      );
    }

    wasmModule.instance = result.instance;
    wasmModule.module = result.module;

    // Run the Go program (this will export functions to window)
    console.log("Running Go program...");
    try {
      // Go.run() is synchronous but the Go program itself may run asynchronously
      goInstance.run(wasmModule.instance);
      console.log("Go program started (run() completed)");
    } catch (error: any) {
      console.error("Failed to run Go program:", error);
      throw new Error(
        `Failed to run Go program: ${error?.message || error}. ` +
          `The WASM module may not be compatible with the current Go runtime.`,
      );
    }

    // Wait for Go to initialize and export functions
    // Go programs can take time to initialize, especially if they have async operations
    console.log("Waiting for Go initialization and function exports...");

    // Poll for exported functions with increasing wait times
    const maxAttempts = 10;
    let exportedFunctions: string[] = [];

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      const waitTime = Math.min(attempt * 200, 2000); // 200ms, 400ms, ..., up to 2s
      await new Promise((resolve) => setTimeout(resolve, waitTime));

      // Check for all functions on window (not just our expected ones)
      const allWindowFunctions = Object.keys(window).filter(
        (key) => typeof (window as any)[key] === "function",
      );

      // Look for our specific functions
      exportedFunctions = allWindowFunctions.filter(
        (key) =>
          key.startsWith("morm") ||
          key.startsWith("derive") ||
          key.startsWith("generate") ||
          key.startsWith("Generate") || // New Universal Signature Generator functions
          key.startsWith("verify") ||
          key.startsWith("map") ||
          key.startsWith("validate") ||
          key === "GetSupportedSignatureTypes" ||
          key === "IsSignatureTypeSupported",
      );

      console.log(`Attempt ${attempt}/${maxAttempts}:`, {
        waitTime: `${waitTime}ms`,
        allWindowFunctions: allWindowFunctions.length,
        exportedFunctions: exportedFunctions,
        sampleWindowKeys: allWindowFunctions.slice(0, 20), // Show first 20 for debugging
      });

      if (exportedFunctions.length > 0) {
        console.log("✅ Go functions found!", exportedFunctions);
        break;
      }

      // On last attempt, show all window functions for debugging
      if (attempt === maxAttempts) {
        console.warn(
          "⚠️ No expected functions found. All window functions:",
          allWindowFunctions.sort(),
        );
        console.warn(
          "⚠️ Check if functions are exported with different names or if there are Go runtime errors",
        );
      }
    }

    if (exportedFunctions.length === 0) {
      // Check for any errors that might have occurred
      const errorMessages: string[] = [];

      // Check if there are any console errors we can detect
      const allWindowKeys = Object.keys(window);
      console.error("Debugging info:", {
        totalWindowKeys: allWindowKeys.length,
        allFunctions: allWindowKeys.filter(
          (k) => typeof (window as any)[k] === "function",
        ),
        hasGo: typeof window.Go === "function",
        wasmInstance: wasmModule.instance !== null,
        wasmModule: wasmModule.module !== null,
      });

      // Try to call InitWASMSigner if it exists (some Go builds export it)
      if (typeof (window as any).InitWASMSigner === "function") {
        console.log("Found InitWASMSigner function, calling it...");
        try {
          (window as any).InitWASMSigner();
          // Wait a bit more after initialization
          await new Promise((resolve) => setTimeout(resolve, 500));

          // Check again
          const functionsAfterInit = Object.keys(window).filter(
            (key) =>
              typeof (window as any)[key] === "function" &&
              (key.startsWith("morm") ||
                key.startsWith("derive") ||
                key.startsWith("generate") ||
                key.startsWith("Generate") ||
                key === "GetSupportedSignatureTypes" ||
                key === "IsSignatureTypeSupported"),
          );

          if (functionsAfterInit.length > 0) {
            console.log(
              "✅ Functions found after InitWASMSigner!",
              functionsAfterInit,
            );
            exportedFunctions = functionsAfterInit;
          }
        } catch (initError: any) {
          console.error("Failed to call InitWASMSigner:", initError);
        }
      }

      if (exportedFunctions.length === 0) {
        throw new Error(
          "Go functions not exported after initialization.\n\n" +
            "ROOT CAUSE: The Go main() function in main_wasm.go does not call InitWASMSigner(), " +
            "which is required to export functions via js.Global().Set().\n\n" +
            "SOLUTION: The Go code needs to be fixed. In main_wasm.go, the main() function should call:\n" +
            "  InitWASMSigner(CreateTestnetDomain()) // or appropriate domain\n\n" +
            "Current status:\n" +
            `- WASM file loaded: ✅\n` +
            `- Go runtime loaded: ${typeof window.Go === "function" ? "✅" : "❌"}\n` +
            `- Go program run: ✅\n` +
            `- Functions exported: ❌\n\n` +
            "Check the browser console for detailed debugging information.",
        );
      }
    }

    console.log("WASM module loaded and initialized successfully");
    return true;
  } catch (error: any) {
    console.error("Failed to load WASM module:", error);
    // Re-throw the error so it can be caught and displayed properly
    throw new Error(
      error?.message || `Failed to load WASM module: ${String(error)}`,
    );
  }
}

// Check if WASM is loaded
export function isWasmLoaded(): boolean {
  return wasmModule.module !== null && wasmModule.instance !== null;
}

// Get WASM instance (for advanced usage)
export function getWasmInstance(): WebAssembly.Instance | null {
  return wasmModule.instance;
}

// Get WASM module (for advanced usage)
export function getWasmModule(): WebAssembly.Module | null {
  return wasmModule.module;
}

// Utility function to call Go functions
// Note: This requires the Go code to export functions properly
export function callGoFunction(functionName: string, ...args: any[]): any {
  if (!isWasmLoaded()) {
    throw new Error("WASM module not loaded");
  }

  // This is a placeholder - actual implementation depends on Go exports
  console.log(`Calling Go function: ${functionName} with args:`, args);

  // In a real implementation, you would call the exported Go functions here
  // For example: return wasmInstance.exports[functionName](...args);

  return null;
}
